import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api, { handleAPIError } from '../services/api';
import TopicPickerModal from '../components/TopicPickerModal';

// ─── Types ──────────────────────────────────────────────────────────
interface CrawledPage {
  id: number;
  url: string;
  title: string;
  meta_description: string;
  crawled_at: string | null;
}

interface ICPData {
  id: number;
  content: string;
  created_at: string | null;
}

interface KGEntity {
  name: string;
  category: string;
  importance: string;
  search_intent: string;
  keywords: string[];
  current_url_count?: number;
  target_url_count?: number;
  coverage_score?: number;
  covering_urls?: string[];
}

interface KGRelationship {
  source: string;
  target: string;
  relationship: string;
}

interface KnowledgeGraph {
  id: number;
  niche: string;
  total_entities: number;
  total_relationships: number;
  category_counts: Record<string, number>;
  importance_counts: Record<string, number>;
  entities: KGEntity[];
  relationships: KGRelationship[];
  created_at: string | null;
}

interface EntityStats {
  total_entities: number;
  bn_score: number;
  under_covered: number;
  avg_coverage: number;
  total_relationships: number;
}

interface Topic {
  id: number;
  topic: string;
  category: string;
  is_covered: boolean;
  covered_by_url: string;
  search_intent: string;
  priority: string;
  target_keyword: string;
  target_entities: string[];
  rationale: string;
  content_type: string;
  competitive_difficulty: string;
  phase: string;
  section_type: string;
  generation_method: string;
  rpp_composite: number;
  title_tag: string;
  url_slug: string;
  meta_description: string;
  is_quality_node: boolean;
  is_trending_node: boolean;
  publish_order: number;
  publish_phase_week: string;
}

interface TopicalMap {
  id: number;
  project_id: number;
  job_id: number;
  niche: string;
  categories: string[];
  source_context: string;
  central_entity: string;
  central_search_intent: string;
  core_section_name: string;
  outer_section_names: string[];
  generation_methods_used: string[];
  quality_node_count: number;
  trending_node_count: number;
  created_at: string | null;
}

interface TopicalMapStats {
  total: number;
  covered: number;
  gaps: number;
  categories: Record<string, number>;
  phases: Record<string, number>;
  methods: Record<string, number>;
  sections: Record<string, number>;
  quality_nodes: number;
  trending_nodes: number;
}

interface CalendarEntry {
  id: number;
  project_id: number;
  sop_map_id: number;
  sop_topic_id: number;
  article_id: number | null;
  title: string;
  keyword: string;
  scheduled_date: string | null;
  status: string;
  category: string;
  priority: string;
  notes: string;
  created_at: string | null;
  updated_at: string | null;
  article_title: string | null;
  article_word_count: number | null;
  article_wp_url: string | null;
}

interface RunningJob {
  id: number;
  system_type: string;
  status: string;
  progress: number;
  created_at: string | null;
}

interface CheckpointInfo {
  has_checkpoint: boolean;
  failed_job_id?: number;
  checkpoint_pass?: number;
  checkpoint_topics?: number;
  failed_at?: string;
  error_message?: string;
}

interface FactSheetJob {
  id: number;
  system_type: string;
  status: string;
  created_at: string;
}

// ─── Step definitions ───────────────────────────────────────────────
const STEPS = [
  { num: 1, label: 'Crawl', slug: 'crawl' },
  { num: 2, label: 'ICP', slug: 'icp' },
  { num: 3, label: 'Knowledge Graph', slug: 'knowledge-graph' },
  { num: 4, label: 'Entities', slug: 'entities' },
  { num: 5, label: 'Topical Map', slug: 'topical-map' },
  { num: 6, label: 'Content Calendar', slug: 'content-calendar' },
] as const;

const STEP_BY_SLUG: Record<string, number> = STEPS.reduce((acc, s) => {
  acc[s.slug] = s.num;
  return acc;
}, {} as Record<string, number>);
const SLUG_BY_STEP: Record<number, string> = STEPS.reduce((acc, s) => {
  acc[s.num] = s.slug;
  return acc;
}, {} as Record<number, string>);

// ─── Helpers ────────────────────────────────────────────────────────
const importanceBadge = (importance: string) => {
  const colors: Record<string, string> = {
    critical: 'bg-red-100 text-red-700',
    high: 'bg-orange-100 text-orange-700',
    medium: 'bg-blue-100 text-blue-700',
    low: 'bg-gray-100 text-gray-600',
  };
  return colors[importance] || colors.medium;
};

const priorityBadge = (p: string) => importanceBadge(p);

const intentBadge = (intent: string) => {
  const colors: Record<string, string> = {
    informational: 'bg-sky-100 text-sky-700',
    commercial: 'bg-amber-100 text-amber-700',
    transactional: 'bg-green-100 text-green-700',
    navigational: 'bg-purple-100 text-purple-700',
  };
  return colors[intent] || 'bg-gray-100 text-gray-600';
};

const coverageColor = (score: number) => {
  if (score >= 70) return 'bg-green-500';
  if (score >= 40) return 'bg-yellow-500';
  return 'bg-red-500';
};

const statusColor = (status: string) => {
  const m: Record<string, string> = {
    scheduled: 'bg-teal-100 text-teal-700',
    rewrite: 'bg-amber-100 text-amber-700',
    writing: 'bg-blue-100 text-blue-700',
    written: 'bg-indigo-100 text-indigo-700',
    images: 'bg-purple-100 text-purple-700',
    ready: 'bg-green-100 text-green-700',
    published: 'bg-emerald-100 text-emerald-800',
  };
  return m[status] || 'bg-gray-100 text-gray-600';
};

function formatDate(iso: string | null | undefined) {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ─── Component ──────────────────────────────────────────────────────
export default function SOPTopicalMapPage() {
  const { projectId: urlProjectId, sopStep } = useParams<{ projectId: string; sopStep: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Support both new URL structure (/projects/:projectId/agent) and legacy (?project_id=...)
  const projectId = urlProjectId || searchParams.get('project_id');
  const pid = projectId ? parseInt(projectId) : null;

  // Derive initial step from URL (path segment takes priority, then ?step=, then 1)
  const initialStep = (() => {
    if (sopStep && STEP_BY_SLUG[sopStep]) return STEP_BY_SLUG[sopStep];
    const qp = searchParams.get('step');
    if (qp && STEP_BY_SLUG[qp]) return STEP_BY_SLUG[qp];
    if (qp && /^[1-6]$/.test(qp)) return parseInt(qp);
    return 1;
  })();
  const [activeStep, setActiveStepState] = useState(initialStep);

  // Navigate helper that updates URL path segment
  const setActiveStep = useCallback((n: number) => {
    setActiveStepState(n);
    if (urlProjectId) {
      const slug = SLUG_BY_STEP[n] || 'crawl';
      navigate(`/projects/${urlProjectId}/sop-topicalmap/${slug}`, { replace: false });
    }
  }, [navigate, urlProjectId]);

  // Sync activeStep when URL changes (back/forward)
  useEffect(() => {
    if (sopStep && STEP_BY_SLUG[sopStep] && STEP_BY_SLUG[sopStep] !== activeStep) {
      setActiveStepState(STEP_BY_SLUG[sopStep]);
    }
  }, [sopStep]);

  // One-time migration: if URL has legacy ?step= and no sopStep param, rewrite to path segment
  useEffect(() => {
    if (!sopStep && urlProjectId) {
      const qp = searchParams.get('step');
      let n: number | null = null;
      if (qp && STEP_BY_SLUG[qp]) n = STEP_BY_SLUG[qp];
      else if (qp && /^[1-6]$/.test(qp)) n = parseInt(qp);
      if (n) {
        navigate(`/projects/${urlProjectId}/sop-topicalmap/${SLUG_BY_STEP[n]}`, { replace: true });
      } else {
        // Default: land on step 1 slug for clean URL
        navigate(`/projects/${urlProjectId}/sop-topicalmap/${SLUG_BY_STEP[activeStep] || 'crawl'}`, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Running jobs
  const [runningJobs, setRunningJobs] = useState<RunningJob[]>([]);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Step 1: Crawl
  const [crawlUrl, setCrawlUrl] = useState('');
  const [crawledPages, setCrawledPages] = useState<CrawledPage[]>([]);

  // Step 2: ICP
  const [icpData, setIcpData] = useState<ICPData | null>(null);
  const [bizDesc, setBizDesc] = useState('');
  const [icpExpanded, setIcpExpanded] = useState(false);
  const [factSheetJobs, setFactSheetJobs] = useState<FactSheetJob[]>([]);

  // Step 3: KG
  const [kg, setKg] = useState<KnowledgeGraph | null>(null);
  const [kgCategoryFilter, setKgCategoryFilter] = useState('All');

  // Step 4: Entities
  const [entities, setEntities] = useState<KGEntity[]>([]);
  const [entityStats, setEntityStats] = useState<EntityStats | null>(null);
  const [entityFilter, setEntityFilter] = useState<'all' | 'under'>('all');

  // Step 5: Topical Map
  const [topicalMap, setTopicalMap] = useState<TopicalMap | null>(null);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [tmStats, setTmStats] = useState<TopicalMapStats | null>(null);
  const [tmCategoryFilter, setTmCategoryFilter] = useState('All');
  const [tmViewMode, setTmViewMode] = useState<'table' | '3d'>('table');
  const [checkpoint, setCheckpoint] = useState<CheckpointInfo | null>(null);

  // Step 6: Calendar
  const [calendarEntries, setCalendarEntries] = useState<CalendarEntry[]>([]);
  const [calendarStats, setCalendarStats] = useState<{ total: number; by_status: Record<string, number> }>({ total: 0, by_status: {} });
  const [calViewMode, setCalViewMode] = useState<'list' | 'calendar'>('list');
  const [calMonth, setCalMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [calSearch, setCalSearch] = useState('');
  const [showTopicPicker, setShowTopicPicker] = useState(false);
  const [selectedCalIds, setSelectedCalIds] = useState<Set<number>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Completion state
  const stepComplete = {
    1: crawledPages.length > 0,
    2: icpData !== null,
    3: kg !== null,
    4: entityStats !== null,
    5: topicalMap !== null,
    6: calendarEntries.length > 0,
  };

  // ─── Polling for running jobs ─────────────────────────────────────
  const pollJobs = useCallback(async () => {
    if (!pid) return;
    try {
      const res = await api.get(`/sop-topicalmap/running-jobs/${pid}`);
      setRunningJobs(res.data.jobs || []);
      if ((res.data.jobs || []).length === 0 && pollRef.current) {
        // Jobs finished, refresh current step data
        clearInterval(pollRef.current);
        pollRef.current = null;
        loadStepData(activeStep);
      }
    } catch {
      // silent
    }
  }, [pid, activeStep]);

  useEffect(() => {
    if (!pid) return;
    pollJobs();
    pollRef.current = setInterval(pollJobs, 4000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [pid, pollJobs]);

  // ─── Load data when step or pid changes ───────────────────────────
  const loadStepData = useCallback(async (step: number) => {
    if (!pid) return;
    setError('');
    setLoading(true);
    try {
      switch (step) {
        case 1: {
          const res = await api.get(`/sop-topicalmap/crawled-pages/${pid}`);
          setCrawledPages(res.data.pages || []);
          break;
        }
        case 2: {
          const [icpRes, fsRes] = await Promise.all([
            api.get(`/sop-topicalmap/icp/${pid}`),
            api.get(`/projects/${pid}/jobs?system_type=factsagent&status=completed`).catch(() => ({ data: [] })),
          ]);
          if (icpRes.data.icp) setIcpData(icpRes.data.icp);
          setBizDesc(icpRes.data.business_description || '');
          setFactSheetJobs(fsRes.data || []);
          break;
        }
        case 3: {
          const res = await api.get(`/sop-topicalmap/knowledge-graph/${pid}`);
          setKg(res.data.knowledge_graph || null);
          break;
        }
        case 4: {
          const res = await api.get(`/sop-topicalmap/entities/${pid}`);
          setEntities(res.data.entities || []);
          setEntityStats(res.data.stats || null);
          break;
        }
        case 5: {
          const [mapRes, cpRes] = await Promise.all([
            api.get(`/sop-topicalmap/results/${pid}`),
            api.get(`/sop-topicalmap/checkpoint/${pid}`),
          ]);
          setTopicalMap(mapRes.data.topical_map || null);
          setTopics(mapRes.data.topics || []);
          setTmStats(mapRes.data.stats || null);
          setCheckpoint(cpRes.data || null);
          break;
        }
        case 6: {
          const res = await api.get(`/content-calendar/entries/${pid}`);
          setCalendarEntries(res.data.entries || []);
          setCalendarStats(res.data.stats || { total: 0, by_status: {} });
          break;
        }
      }
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setLoading(false);
    }
  }, [pid]);

  useEffect(() => {
    loadStepData(activeStep);
  }, [activeStep, loadStepData]);

  // Also load crawl count on mount for step completion tracking
  useEffect(() => {
    if (!pid) return;
    Promise.all([
      api.get(`/sop-topicalmap/crawled-pages/${pid}`).then(r => setCrawledPages(r.data.pages || [])).catch(() => {}),
      api.get(`/sop-topicalmap/icp/${pid}`).then(r => { if (r.data.icp) setIcpData(r.data.icp); }).catch(() => {}),
      api.get(`/sop-topicalmap/knowledge-graph/${pid}`).then(r => setKg(r.data.knowledge_graph || null)).catch(() => {}),
      api.get(`/sop-topicalmap/entities/${pid}`).then(r => setEntityStats(r.data.stats || null)).catch(() => {}),
      api.get(`/sop-topicalmap/results/${pid}`).then(r => { setTopicalMap(r.data.topical_map || null); setTopics(r.data.topics || []); setTmStats(r.data.stats || null); }).catch(() => {}),
    ]);
  }, [pid]);

  // ─── Helper to start polling after action ─────────────────────────
  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(pollJobs, 4000);
  };

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  // ─── Job type → step label mapping ────────────────────────────────
  const jobTypeLabel = (t: string) => {
    const m: Record<string, string> = {
      sop_crawl: 'Crawl',
      icpagent: 'ICP',
      sop_knowledge_graph: 'Knowledge Graph',
      sop_entity_coverage: 'Entity Coverage',
      sop_topical_map_orchestrator: 'Topical Map',
      sop_topical_map: 'Topical Map',
    };
    return m[t] || t;
  };

  // ─────────────────────────────────────────────────────────────────
  // STEP 1: CRAWL
  // ─────────────────────────────────────────────────────────────────
  const handleStartCrawl = async () => {
    if (!pid || !crawlUrl.trim()) return;
    setError('');
    try {
      const form = new FormData();
      form.append('project_id', String(pid));
      form.append('domain', crawlUrl.trim());
      await api.post('/sop-topicalmap/crawl-url', form);
      showSuccess('Crawl started');
      setCrawlUrl('');
      startPolling();
    } catch (err) {
      setError(handleAPIError(err));
    }
  };

  const handleUploadJSON = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !pid) return;
    setError('');
    try {
      const form = new FormData();
      form.append('project_id', String(pid));
      form.append('file', file);
      const res = await api.post('/sop-topicalmap/crawl', form);
      showSuccess(`Uploaded ${res.data.pages_added} pages`);
      loadStepData(1);
    } catch (err) {
      setError(handleAPIError(err));
    }
    e.target.value = '';
  };

  const handleDeleteAllPages = async () => {
    if (!pid || !confirm('Delete all crawled pages?')) return;
    try {
      await api.delete(`/sop-topicalmap/crawled-pages/${pid}`);
      setCrawledPages([]);
      showSuccess('All pages deleted');
    } catch (err) {
      setError(handleAPIError(err));
    }
  };

  // ─────────────────────────────────────────────────────────────────
  // STEP 2: ICP
  // ─────────────────────────────────────────────────────────────────
  const handleLoadFactSheet = async (jobId: number) => {
    if (!pid) return;
    try {
      const res = await api.get(`/factsagent/results/${jobId}`);
      const outputs = res.data?.outputs || [];
      // Get the final step (step 6) content
      const finalStep = outputs.find((o: any) => o.step_number === 6);
      if (finalStep?.content_text) {
        setBizDesc(finalStep.content_text);
      } else if (outputs.length > 0) {
        const last = outputs[outputs.length - 1];
        setBizDesc(last.content_text || '');
      }
    } catch (err) {
      setError(handleAPIError(err));
    }
  };

  const handleSaveBusinessContext = async () => {
    if (!pid) return;
    try {
      await api.post(`/sop-topicalmap/icp/${pid}`, { business_description: bizDesc });
      showSuccess('Business context saved');
    } catch (err) {
      setError(handleAPIError(err));
    }
  };

  const handleRegenerateICP = async () => {
    if (!pid) return;
    setError('');
    try {
      await api.post(`/sop-topicalmap/icp/generate/${pid}`);
      showSuccess('ICP generation started');
      startPolling();
    } catch (err) {
      setError(handleAPIError(err));
    }
  };

  // ─────────────────────────────────────────────────────────────────
  // STEP 3: KNOWLEDGE GRAPH
  // ─────────────────────────────────────────────────────────────────
  const handleRebuildKG = async () => {
    if (!pid) return;
    setError('');
    try {
      await api.post(`/sop-topicalmap/knowledge-graph/${pid}`);
      showSuccess('Knowledge Graph generation started');
      startPolling();
    } catch (err) {
      setError(handleAPIError(err));
    }
  };

  // ─────────────────────────────────────────────────────────────────
  // STEP 4: ENTITIES
  // ─────────────────────────────────────────────────────────────────
  const handleReScoreEntities = async () => {
    if (!pid) return;
    setError('');
    try {
      await api.post(`/sop-topicalmap/entities/${pid}`);
      showSuccess('Entity scoring started');
      startPolling();
    } catch (err) {
      setError(handleAPIError(err));
    }
  };

  // ─────────────────────────────────────────────────────────────────
  // STEP 5: TOPICAL MAP
  // ─────────────────────────────────────────────────────────────────
  const handleGenerateMap = async () => {
    if (!pid) return;
    setError('');
    try {
      const form = new FormData();
      form.append('project_id', String(pid));
      await api.post('/sop-topicalmap/generate', form);
      showSuccess('Topical Map generation started');
      startPolling();
    } catch (err) {
      setError(handleAPIError(err));
    }
  };

  const handleResumeMap = async () => {
    if (!pid) return;
    setError('');
    try {
      const form = new FormData();
      form.append('project_id', String(pid));
      const res = await api.post('/sop-topicalmap/resume', form);
      showSuccess(res.data.message || 'Resumed');
      startPolling();
    } catch (err) {
      setError(handleAPIError(err));
    }
  };

  const handleDeleteMap = async () => {
    if (!topicalMap || !confirm('Delete this topical map and all its topics?')) return;
    try {
      await api.delete(`/sop-topicalmap/${topicalMap.id}`);
      setTopicalMap(null);
      setTopics([]);
      setTmStats(null);
      showSuccess('Topical map deleted');
    } catch (err) {
      setError(handleAPIError(err));
    }
  };

  const handleExportCSV = () => {
    if (!pid) return;
    const token = localStorage.getItem('auth_token') || '';
    window.open(`/api/sop-topicalmap/export-csv/${pid}?token=${token}`, '_blank');
  };

  const handleExportJSON = () => {
    if (!pid) return;
    const token = localStorage.getItem('auth_token') || '';
    window.open(`/api/sop-topicalmap/export/${pid}?token=${token}`, '_blank');
  };

  const handleImportCSV = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !pid) return;
    setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post(`/sop-topicalmap/import-csv/${pid}`, form);
      showSuccess(`Imported ${res.data.added} topics (${res.data.skipped} duplicates skipped)`);
      loadStepData(5);
    } catch (err) {
      setError(handleAPIError(err));
    }
    e.target.value = '';
  };

  // ─────────────────────────────────────────────────────────────────
  // STEP 6: CONTENT CALENDAR
  // ─────────────────────────────────────────────────────────────────
  const loadCalendar = useCallback(async () => {
    if (!pid) return;
    try {
      const res = await api.get(`/content-calendar/entries/${pid}?month=${calMonth}`);
      setCalendarEntries(res.data.entries || []);
      setCalendarStats(res.data.stats || { total: 0, by_status: {} });
    } catch (err) {
      setError(handleAPIError(err));
    }
  }, [pid, calMonth]);

  useEffect(() => {
    if (activeStep === 6) loadCalendar();
  }, [activeStep, loadCalendar]);

  const handleShareCalendar = async () => {
    if (!pid) return;
    try {
      const res = await api.post(`/content-calendar/share/${pid}`);
      const shareUrl = `${window.location.origin}/api/content-calendar/public/${res.data.share_token}`;
      navigator.clipboard.writeText(shareUrl);
      showSuccess('Share link copied to clipboard!');
    } catch (err) {
      setError(handleAPIError(err));
    }
  };

  const handleTopicsSelected = async (items: Array<{ topic: any; scheduled_date: string }>) => {
    if (!pid) return;
    try {
      // Backend expects /content-calendar/entries (no project_id in path)
      // with body { project_id, sop_map_id, entries: [{ sop_topic_id, scheduled_date, notes? }] }
      const entries = items.map(({ topic, scheduled_date }) => ({
        sop_topic_id: topic.id,
        scheduled_date,
        notes: '',
      }));

      await api.post(`/content-calendar/entries`, {
        project_id: Number(pid),
        sop_map_id: topicalMap?.id || 0,
        entries,
      });
      showSuccess(`Added ${entries.length} topics to calendar`);
      loadCalendar();
    } catch (err) {
      setError(handleAPIError(err));
    }
  };

  const handleUpdateEntryStatus = async (entryId: number, status: string) => {
    try {
      await api.put(`/content-calendar/entries/${entryId}`, { status });
      loadCalendar();
    } catch (err) {
      setError(handleAPIError(err));
    }
  };

  const handleDeleteEntry = async (entryId: number) => {
    try {
      await api.delete(`/content-calendar/entries/${entryId}`);
      setSelectedCalIds(prev => {
        const n = new Set(prev);
        n.delete(entryId);
        return n;
      });
      loadCalendar();
    } catch (err) {
      setError(handleAPIError(err));
    }
  };

  const toggleCalSelect = (id: number) => {
    setSelectedCalIds(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const toggleCalSelectAll = () => {
    // Operates over the currently filtered entries visible in the list
    const ids = filteredCalEntries.map(e => e.id);
    const allSelected = ids.length > 0 && ids.every(id => selectedCalIds.has(id));
    setSelectedCalIds(prev => {
      const n = new Set(prev);
      if (allSelected) ids.forEach(id => n.delete(id));
      else ids.forEach(id => n.add(id));
      return n;
    });
  };

  const handleBulkDeleteCal = async () => {
    if (!pid || selectedCalIds.size === 0) return;
    const count = selectedCalIds.size;
    if (!window.confirm(`Delete ${count} calendar ${count === 1 ? 'entry' : 'entries'}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      await api.post(`/content-calendar/entries/bulk-delete/${pid}`, {
        ids: Array.from(selectedCalIds),
      });
      setSelectedCalIds(new Set());
      showSuccess(`Deleted ${count} ${count === 1 ? 'entry' : 'entries'}`);
      loadCalendar();
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setBulkDeleting(false);
    }
  };

  // Calendar grid helpers
  const calMonthDate = new Date(calMonth + '-01');
  const calYear = calMonthDate.getFullYear();
  const calMonthNum = calMonthDate.getMonth();
  const firstDayOfWeek = new Date(calYear, calMonthNum, 1).getDay();
  const daysInMonth = new Date(calYear, calMonthNum + 1, 0).getDate();
  const calendarGrid: (number | null)[] = [];
  for (let i = 0; i < firstDayOfWeek; i++) calendarGrid.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendarGrid.push(d);
  while (calendarGrid.length % 7 !== 0) calendarGrid.push(null);

  const entriesByDate: Record<string, CalendarEntry[]> = {};
  calendarEntries.forEach(e => {
    if (e.scheduled_date) {
      const key = e.scheduled_date;
      if (!entriesByDate[key]) entriesByDate[key] = [];
      entriesByDate[key].push(e);
    }
  });

  const prevMonth = () => {
    const d = new Date(calMonth + '-01');
    d.setMonth(d.getMonth() - 1);
    setCalMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };
  const nextMonth = () => {
    const d = new Date(calMonth + '-01');
    d.setMonth(d.getMonth() + 1);
    setCalMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  // Sort state for Step 5 table. key=column id, dir='asc'|'desc'|null (null = unsorted)
  const [tmSortKey, setTmSortKey] = useState<string | null>(null);
  const [tmSortDir, setTmSortDir] = useState<'asc' | 'desc' | null>(null);

  // Add-topic modal state
  const [showAddTopic, setShowAddTopic] = useState(false);
  const [newTopic, setNewTopic] = useState({
    topic: '',
    target_keyword: '',
    category: '',
    search_intent: 'informational',
    priority: 'medium',
    section_type: 'outer',
    phase: 'expansion',
  });
  const [addingTopic, setAddingTopic] = useState(false);

  const handleAddTopic = async () => {
    if (!pid || !newTopic.topic.trim()) return;
    setAddingTopic(true);
    try {
      await api.post(`/sop-topicalmap/topics/add/${pid}`, newTopic);
      showSuccess('Topic added');
      setShowAddTopic(false);
      setNewTopic({
        topic: '', target_keyword: '', category: '',
        search_intent: 'informational', priority: 'medium',
        section_type: 'outer', phase: 'expansion',
      });
      // Reload topics
      const r = await api.get(`/sop-topicalmap/results/${pid}`);
      setTopicalMap(r.data.topical_map || null);
      setTopics(r.data.topics || []);
      setTmStats(r.data.stats || null);
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setAddingTopic(false);
    }
  };

  const handleDeleteTopic = async (topicId: number, topicName: string) => {
    if (!confirm(`Delete topic "${topicName}"?\n\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/sop-topicalmap/topics/delete/${topicId}`);
      setTopics(prev => prev.filter(t => t.id !== topicId));
      showSuccess('Topic deleted');
    } catch (err) {
      setError(handleAPIError(err));
    }
  };

  const toggleTmSort = (key: string) => {
    if (tmSortKey !== key) {
      setTmSortKey(key);
      setTmSortDir('asc');
      return;
    }
    // same column — cycle asc → desc → off
    if (tmSortDir === 'asc') setTmSortDir('desc');
    else if (tmSortDir === 'desc') { setTmSortKey(null); setTmSortDir(null); }
    else setTmSortDir('asc');
  };

  // Filtered + sorted topics for Step 5
  const filteredTopics = (() => {
    const base = topics.filter(t => {
      if (tmCategoryFilter !== 'All' && t.category !== tmCategoryFilter) return false;
      return true;
    });
    if (!tmSortKey || !tmSortDir) return base;

    const priorityRank: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1 };
    const intentRank: Record<string, number> = {
      transactional: 4, commercial: 3, informational: 2, navigational: 1,
    };

    const getVal = (t: any): string | number => {
      switch (tmSortKey) {
        case '#': return Number(t.publish_order || 0);
        case 'Topic': return String(t.topic || '').toLowerCase();
        case 'Keyword': return String(t.target_keyword || '').toLowerCase();
        case 'Category': return String(t.category || '').toLowerCase();
        case 'Intent': return intentRank[String(t.search_intent || '').toLowerCase()] || 0;
        case 'Priority': return priorityRank[String(t.priority || '').toLowerCase()] || 0;
        case 'Section': return String(t.section_type || '').toLowerCase();
        case 'Phase': return String(t.phase || '').toLowerCase();
        case 'Covered': return t.is_covered ? 1 : 0;
        case 'URL': return String(t.covered_by_url || '').toLowerCase();
        default: return '';
      }
    };

    const sorted = [...base].sort((a, b) => {
      const av = getVal(a);
      const bv = getVal(b);
      if (av < bv) return tmSortDir === 'asc' ? -1 : 1;
      if (av > bv) return tmSortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  })();

  // Filtered entities for Step 4
  const filteredEntities = entities.filter(e => {
    if (entityFilter === 'under' && (e.coverage_score || 0) >= 30) return false;
    return true;
  });

  // Filtered calendar entries
  const filteredCalEntries = calendarEntries.filter(e => {
    if (!calSearch.trim()) return true;
    const s = calSearch.toLowerCase();
    return (e.title || '').toLowerCase().includes(s) || (e.keyword || '').toLowerCase().includes(s);
  });

  // ─────────────────────────────────────────────────────────────────
  // No project guard
  // ─────────────────────────────────────────────────────────────────
  if (!pid) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-700 mb-2">No project selected</h2>
            <p className="text-gray-500">Select a project from the Projects page to get started.</p>
          </div>
        </div>
      </Layout>
    );
  }

  // ─────────────────────────────────────────────────────────────────
  // Running jobs banner
  // ─────────────────────────────────────────────────────────────────
  const RunningJobsBanner = () => {
    if (runningJobs.length === 0) return null;
    return (
      <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-sm font-medium text-blue-800">Running jobs:</span>
          {runningJobs.map(j => (
            <span key={j.id} className="text-sm text-blue-700">
              {jobTypeLabel(j.system_type)} ({j.status}{j.progress ? ` ${j.progress}%` : ''})
            </span>
          ))}
        </div>
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────
  return (
    <Layout projectId={pid}>
      <div className="max-w-[1400px] mx-auto">
        {/* Page Title */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">SOP Topical Map</h1>
          <p className="text-sm text-gray-500 mt-1">6-step pipeline: Crawl, ICP, Knowledge Graph, Entity Coverage, Topical Map, Content Calendar</p>
        </div>

        {/* Tab Bar */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex gap-0 -mb-px overflow-x-auto">
            {STEPS.map(step => {
              const isActive = activeStep === step.num;
              const isComplete = stepComplete[step.num as keyof typeof stepComplete];
              return (
                <button
                  key={step.num}
                  onClick={() => setActiveStep(step.num)}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                    isActive
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {isComplete ? (
                    <span className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-xs">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                  ) : (
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      isActive ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {step.num}
                    </span>
                  )}
                  {step.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center justify-between">
            <span className="text-sm text-red-700">{error}</span>
            <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 text-lg leading-none">&times;</button>
          </div>
        )}
        {successMsg && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3">
            <span className="text-sm text-green-700">{successMsg}</span>
          </div>
        )}

        <RunningJobsBanner />

        {loading && (
          <div className="flex items-center justify-center h-40">
            <svg className="w-8 h-8 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* STEP 1: CRAWL */}
        {/* ═══════════════════════════════════════════════════════════ */}
        {!loading && activeStep === 1 && (
          <div className="space-y-6">
            {/* Input row */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[300px]">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Website URL</label>
                  <input
                    type="text"
                    value={crawlUrl}
                    onChange={e => setCrawlUrl(e.target.value)}
                    placeholder="https://example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    onKeyDown={e => e.key === 'Enter' && handleStartCrawl()}
                  />
                </div>
                <button
                  onClick={handleStartCrawl}
                  disabled={!crawlUrl.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Start Full Crawl
                </button>
                <label className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 cursor-pointer border border-gray-300">
                  Upload JSON
                  <input type="file" accept=".json" className="hidden" onChange={handleUploadJSON} />
                </label>
              </div>
            </div>

            {/* Crawled pages table */}
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <h3 className="font-semibold text-gray-900">Crawled Pages</h3>
                  <span className="px-2.5 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                    {crawledPages.length} pages
                  </span>
                </div>
                {crawledPages.length > 0 && (
                  <button onClick={handleDeleteAllPages} className="text-sm text-red-500 hover:text-red-700">
                    Delete All Pages
                  </button>
                )}
              </div>
              {crawledPages.length === 0 ? (
                <div className="p-8 text-center text-gray-400 text-sm">
                  No pages crawled yet. Enter a URL above to start crawling, or upload a JSON file.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-left">
                        <th className="px-4 py-3 font-medium text-gray-500 uppercase text-xs">URL</th>
                        <th className="px-4 py-3 font-medium text-gray-500 uppercase text-xs">Title</th>
                        <th className="px-4 py-3 font-medium text-gray-500 uppercase text-xs w-32">Crawled</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {crawledPages.map(p => (
                        <tr key={p.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2.5 text-blue-600 truncate max-w-[400px]">
                            <a href={p.url} target="_blank" rel="noopener noreferrer" className="hover:underline">{p.url}</a>
                          </td>
                          <td className="px-4 py-2.5 text-gray-700 truncate max-w-[300px]">{p.title || '-'}</td>
                          <td className="px-4 py-2.5 text-gray-500 text-xs">{formatDate(p.crawled_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* STEP 2: ICP */}
        {/* ═══════════════════════════════════════════════════════════ */}
        {!loading && activeStep === 2 && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
              {/* Load from fact sheet */}
              {factSheetJobs.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Load from Fact Sheet</label>
                  <select
                    onChange={e => { if (e.target.value) handleLoadFactSheet(parseInt(e.target.value)); }}
                    className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    defaultValue=""
                  >
                    <option value="">-- Select a fact sheet --</option>
                    {factSheetJobs.map(j => (
                      <option key={j.id} value={j.id}>Job #{j.id} - {formatDate(j.created_at)}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Business description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Business Description</label>
                <textarea
                  value={bizDesc}
                  onChange={e => setBizDesc(e.target.value)}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Describe the business, its services, target audience, location, etc."
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleSaveBusinessContext}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                >
                  Save Business Context
                </button>
                <button
                  onClick={handleRegenerateICP}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700"
                >
                  Regenerate ICP
                </button>
              </div>
            </div>

            {/* ICP output */}
            {icpData && (
              <div className="bg-white rounded-xl border border-gray-200">
                <button
                  onClick={() => setIcpExpanded(!icpExpanded)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    <h3 className="font-semibold text-gray-900">Generated ICP Document</h3>
                    <span className="px-2.5 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                      {icpData.content.split(/\s+/).length} words
                    </span>
                  </div>
                  <svg className={`w-5 h-5 text-gray-400 transition-transform ${icpExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {icpExpanded && (
                  <div className="px-4 pb-4 border-t border-gray-100">
                    <div className="mt-3 prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
                      {icpData.content}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* STEP 3: KNOWLEDGE GRAPH */}
        {/* ═══════════════════════════════════════════════════════════ */}
        {!loading && activeStep === 3 && (
          <div className="space-y-6">
            {/* Stats */}
            {kg && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Entities</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{kg.total_entities}</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Relationships</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{kg.total_relationships}</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Categories</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{Object.keys(kg.category_counts || {}).length}</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Critical</p>
                  <p className="text-2xl font-bold text-red-600 mt-1">{(kg.importance_counts || {}).critical || 0}</p>
                </div>
              </div>
            )}

            {/* Action + filters */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setKgCategoryFilter('All')}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    kgCategoryFilter === 'All' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  All
                </button>
                {kg && Object.entries(kg.category_counts || {}).map(([cat, count]) => (
                  <button
                    key={cat}
                    onClick={() => setKgCategoryFilter(cat)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      kgCategoryFilter === cat ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {cat} ({count})
                  </button>
                ))}
              </div>
              <button
                onClick={handleRebuildKG}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700"
              >
                Rebuild Knowledge Graph
              </button>
            </div>

            {/* Entities table */}
            {kg && kg.entities.length > 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left">
                      <th className="px-4 py-3 font-medium text-gray-500 uppercase text-xs">Entity</th>
                      <th className="px-4 py-3 font-medium text-gray-500 uppercase text-xs">Category</th>
                      <th className="px-4 py-3 font-medium text-gray-500 uppercase text-xs">Importance</th>
                      <th className="px-4 py-3 font-medium text-gray-500 uppercase text-xs">Intent</th>
                      <th className="px-4 py-3 font-medium text-gray-500 uppercase text-xs">Keywords</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {kg.entities
                      .filter(e => kgCategoryFilter === 'All' || e.category === kgCategoryFilter)
                      .map((e, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-4 py-2.5 font-medium text-gray-900">{e.name}</td>
                          <td className="px-4 py-2.5 text-gray-600">{e.category}</td>
                          <td className="px-4 py-2.5">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${importanceBadge(e.importance)}`}>
                              {e.importance}
                            </span>
                          </td>
                          <td className="px-4 py-2.5">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${intentBadge(e.search_intent)}`}>
                              {e.search_intent}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-gray-500 text-xs max-w-[250px] truncate">
                            {(e.keywords || []).join(', ')}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            ) : !kg ? (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                <p className="text-gray-400 text-sm mb-4">No Knowledge Graph generated yet.</p>
                <button
                  onClick={handleRebuildKG}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700"
                >
                  Generate Knowledge Graph
                </button>
              </div>
            ) : null}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* STEP 4: ENTITIES (Entity Coverage) */}
        {/* ═══════════════════════════════════════════════════════════ */}
        {!loading && activeStep === 4 && (
          <div className="space-y-6">
            {/* Stats */}
            {entityStats && (
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">BN Score</p>
                  <p className="text-2xl font-bold text-blue-600 mt-1">{entityStats.bn_score}%</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Total Entities</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{entityStats.total_entities}</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Relationships</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{entityStats.total_relationships}</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Under-Covered</p>
                  <p className="text-2xl font-bold text-red-600 mt-1">{entityStats.under_covered}</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4 col-span-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Avg Coverage</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{entityStats.avg_coverage}%</p>
                </div>
              </div>
            )}

            {/* Filters + action */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between gap-3">
              <div className="flex gap-2">
                <button
                  onClick={() => setEntityFilter('all')}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                    entityFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  All ({entities.length})
                </button>
                <button
                  onClick={() => setEntityFilter('under')}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                    entityFilter === 'under' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Under-Covered ({entities.filter(e => (e.coverage_score || 0) < 30).length})
                </button>
              </div>
              <button
                onClick={handleReScoreEntities}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700"
              >
                Re-Score Entities
              </button>
            </div>

            {/* Entities table */}
            {filteredEntities.length > 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left">
                      <th className="px-4 py-3 font-medium text-gray-500 uppercase text-xs">Entity</th>
                      <th className="px-4 py-3 font-medium text-gray-500 uppercase text-xs">Category</th>
                      <th className="px-4 py-3 font-medium text-gray-500 uppercase text-xs">Importance</th>
                      <th className="px-4 py-3 font-medium text-gray-500 uppercase text-xs w-48">Coverage</th>
                      <th className="px-4 py-3 font-medium text-gray-500 uppercase text-xs">URLs</th>
                      <th className="px-4 py-3 font-medium text-gray-500 uppercase text-xs">Keywords</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredEntities.map((e, i) => {
                      const score = e.coverage_score || 0;
                      const current = e.current_url_count || 0;
                      const target = e.target_url_count || 3;
                      return (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-4 py-2.5 font-medium text-gray-900">{e.name}</td>
                          <td className="px-4 py-2.5 text-gray-600">{e.category}</td>
                          <td className="px-4 py-2.5">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${importanceBadge(e.importance)}`}>
                              {e.importance}
                            </span>
                          </td>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${coverageColor(score)}`}
                                  style={{ width: `${Math.min(score, 100)}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-500 w-10 text-right">{score}%</span>
                            </div>
                          </td>
                          <td className="px-4 py-2.5 text-gray-600 text-xs">{current}/{target}</td>
                          <td className="px-4 py-2.5 text-gray-500 text-xs max-w-[200px] truncate">
                            {(e.keywords || []).join(', ')}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                <p className="text-gray-400 text-sm mb-4">No entity coverage data yet.</p>
                <button
                  onClick={handleReScoreEntities}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700"
                >
                  Score Entity Coverage
                </button>
              </div>
            )}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* STEP 5: TOPICAL MAP */}
        {/* ═══════════════════════════════════════════════════════════ */}
        {!loading && activeStep === 5 && (
          <div className="space-y-6">
            {/* Stats */}
            {tmStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Total Topics</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{tmStats.total}</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Covered</p>
                  <p className="text-2xl font-bold text-green-600 mt-1">{tmStats.covered}</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Missing</p>
                  <p className="text-2xl font-bold text-red-600 mt-1">{tmStats.gaps}</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">High Priority Gaps</p>
                  <p className="text-2xl font-bold text-red-600 mt-1">
                    {topics.filter(t => !t.is_covered && (t.priority === 'critical' || t.priority === 'high')).length}
                  </p>
                </div>
              </div>
            )}

            {/* Business context */}
            {topicalMap?.source_context && (
              <div className="bg-white rounded-xl border-l-4 border-l-blue-500 border border-gray-200 p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Business Context</p>
                <p className="text-sm text-gray-700 line-clamp-3">{topicalMap.source_context}</p>
              </div>
            )}

            {/* Actions bar */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                {/* View toggle */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setTmViewMode('table')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                      tmViewMode === 'table' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Table View
                  </button>
                  <button
                    onClick={() => setTmViewMode('3d')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                      tmViewMode === '3d' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    3D Map View
                  </button>
                </div>

                {/* Action buttons */}
                <div className="flex flex-wrap gap-2">
                  <button onClick={handleExportCSV} className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200 border border-gray-300">
                    CSV
                  </button>
                  <button onClick={handleExportJSON} className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200 border border-gray-300">
                    JSON
                  </button>
                  <label className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200 border border-gray-300 cursor-pointer">
                    Import CSV
                    <input type="file" accept=".csv" className="hidden" onChange={handleImportCSV} />
                  </label>
                  {topicalMap && (
                    <button onClick={() => setShowAddTopic(true)} className="px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-xs font-medium hover:bg-green-100 border border-green-200">
                      + Add Topic
                    </button>
                  )}
                  {topicalMap && (
                    <button onClick={handleDeleteMap} className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-xs font-medium hover:bg-red-100 border border-red-200">
                      Delete Map
                    </button>
                  )}
                  {checkpoint?.has_checkpoint && (
                    <button onClick={handleResumeMap} className="px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg text-xs font-medium hover:bg-amber-100 border border-amber-200">
                      Resume from Pass {checkpoint.checkpoint_pass}
                    </button>
                  )}
                  <button onClick={handleGenerateMap} className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700">
                    {topicalMap ? 'Regenerate' : 'Generate'} Topical Map
                  </button>
                </div>
              </div>

              {/* Category filter pills */}
              {tmStats && Object.keys(tmStats.categories).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setTmCategoryFilter('All')}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      tmCategoryFilter === 'All' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    All ({tmStats.total})
                  </button>
                  {Object.entries(tmStats.categories).sort((a, b) => b[1] - a[1]).map(([cat, count]) => (
                    <button
                      key={cat}
                      onClick={() => setTmCategoryFilter(cat)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                        tmCategoryFilter === cat ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {cat} ({count})
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Topics table */}
            {tmViewMode === 'table' && filteredTopics.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
                <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: '48px' }} />
                    <col style={{ width: '260px' }} />
                    <col style={{ width: '220px' }} />
                    <col style={{ width: '140px' }} />
                    <col style={{ width: '110px' }} />
                    <col style={{ width: '110px' }} />
                    <col style={{ width: '120px' }} />
                    <col style={{ width: '110px' }} />
                    <col style={{ width: '90px' }} />
                    <col style={{ width: '260px' }} />
                    <col style={{ width: '70px' }} />
                  </colgroup>
                  <thead>
                    <tr className="bg-gray-50 text-left">
                      {(['#', 'Topic', 'Keyword', 'Category', 'Intent', 'Priority', 'Section', 'Phase', 'Covered', 'URL'] as const).map((col) => {
                        const minW: Record<string, number> = {
                          '#': 40, Topic: 120, Keyword: 120, Category: 100,
                          Intent: 90, Priority: 90, Section: 100, Phase: 90,
                          Covered: 80, URL: 120,
                        };
                        const active = tmSortKey === col;
                        const arrow = !active ? (
                          <span className="text-gray-300 ml-1">↕</span>
                        ) : tmSortDir === 'asc' ? (
                          <span className="text-gray-700 ml-1">▲</span>
                        ) : tmSortDir === 'desc' ? (
                          <span className="text-gray-700 ml-1">▼</span>
                        ) : (
                          <span className="text-gray-300 ml-1">↕</span>
                        );
                        return (
                          <th
                            key={col}
                            className="px-4 py-3 font-medium text-gray-500 uppercase text-xs select-none"
                            style={{ resize: 'horizontal', overflow: 'auto', minWidth: minW[col] }}
                          >
                            <button
                              type="button"
                              onClick={() => toggleTmSort(col)}
                              className="flex items-center gap-0.5 uppercase font-medium hover:text-gray-800 cursor-pointer"
                              title={active ? `Sorted ${tmSortDir} — click to ${tmSortDir === 'asc' ? 'reverse' : 'clear'}` : 'Click to sort'}
                            >
                              {col}
                              {arrow}
                            </button>
                          </th>
                        );
                      })}
                      <th className="px-2 py-3 font-medium text-gray-500 uppercase text-xs text-center" style={{ minWidth: 60 }}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredTopics.map((t, i) => (
                      <tr key={t.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2.5 text-gray-400 text-xs">{t.publish_order || i + 1}</td>
                        <td className="px-4 py-2.5" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                          <div className="font-medium text-gray-900">{t.topic}</div>
                          {t.title_tag && <div className="text-xs text-gray-400">{t.title_tag}</div>}
                        </td>
                        <td
                          className="px-4 py-2.5 text-gray-700 text-xs font-mono"
                          style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                          title={t.target_keyword}
                        >
                          {t.target_keyword}
                        </td>
                        <td className="px-4 py-2.5 text-gray-600 text-xs" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{t.category}</td>
                        <td className="px-4 py-2.5">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${intentBadge(t.search_intent)}`}>
                            {t.search_intent}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priorityBadge(t.priority)}`}>
                            {t.priority}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-gray-600 text-xs capitalize" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{t.section_type}</td>
                        <td className="px-4 py-2.5 text-gray-600 text-xs capitalize">{t.phase}</td>
                        <td className="px-4 py-2.5">
                          {t.is_covered ? (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Yes</span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-600">No</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-xs" style={{ whiteSpace: 'normal', wordBreak: 'break-all' }}>
                          {t.covered_by_url ? (
                            <a
                              href={t.covered_by_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={t.covered_by_url}
                              className="text-blue-600 hover:text-blue-800 hover:underline block"
                            >
                              {t.covered_by_url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                            </a>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-2 py-2.5 text-center">
                          <button
                            onClick={() => handleDeleteTopic(t.id, t.topic)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded p-1"
                            title="Delete topic"
                          >
                            🗑
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {tmViewMode === '3d' && topicalMap && (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                <p className="text-gray-500 text-sm">3D visualization loads in a separate viewer.</p>
                <p className="text-gray-400 text-xs mt-1">Use the API endpoint <code>/api/sop-topicalmap/visualization/{topicalMap.id}</code> to fetch 3D data.</p>
              </div>
            )}

            {!topicalMap && (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                <p className="text-gray-400 text-sm mb-4">No topical map generated yet.</p>
                <button
                  onClick={handleGenerateMap}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                >
                  Generate Topical Map
                </button>
              </div>
            )}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* STEP 6: CONTENT CALENDAR */}
        {/* ═══════════════════════════════════════════════════════════ */}
        {!loading && activeStep === 6 && (
          <div className="space-y-6">
            {/* Header actions */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setShowTopicPicker(true)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
                >
                  Add from Topical Map
                </button>
                <button
                  onClick={handleShareCalendar}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 border border-gray-300"
                >
                  Share Calendar
                </button>
                {selectedCalIds.size > 0 && (
                  <button
                    onClick={handleBulkDeleteCal}
                    disabled={bulkDeleting}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-60"
                  >
                    {bulkDeleting ? 'Deleting...' : `Delete Selected (${selectedCalIds.size})`}
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCalViewMode('list')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                    calViewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  List View
                </button>
                <button
                  onClick={() => setCalViewMode('calendar')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                    calViewMode === 'calendar' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Calendar View
                </button>
              </div>
            </div>

            {/* Stats bar */}
            <div className="flex flex-wrap gap-3">
              {[
                { label: 'Total', count: calendarStats.total, color: 'bg-gray-100 text-gray-700' },
                { label: 'Scheduled', count: calendarStats.by_status.scheduled || 0, color: 'bg-teal-100 text-teal-700' },
                { label: 'Rewrite', count: calendarStats.by_status.rewrite || 0, color: 'bg-amber-100 text-amber-700' },
                { label: 'Writing', count: calendarStats.by_status.writing || 0, color: 'bg-blue-100 text-blue-700' },
                { label: 'Written', count: calendarStats.by_status.written || 0, color: 'bg-indigo-100 text-indigo-700' },
                { label: 'Images', count: calendarStats.by_status.images || 0, color: 'bg-purple-100 text-purple-700' },
                { label: 'Ready', count: calendarStats.by_status.ready || 0, color: 'bg-green-100 text-green-700' },
                { label: 'Published', count: calendarStats.by_status.published || 0, color: 'bg-emerald-100 text-emerald-700' },
              ].map(s => (
                <span key={s.label} className={`px-3 py-1.5 rounded-full text-xs font-medium ${s.color}`}>
                  {s.label}: {s.count}
                </span>
              ))}
            </div>

            {/* Month nav + search */}
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <button onClick={prevMonth} className="p-1.5 hover:bg-gray-100 rounded-lg">
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <span className="text-sm font-semibold text-gray-700 min-w-[120px] text-center">
                  {new Date(calMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </span>
                <button onClick={nextMonth} className="p-1.5 hover:bg-gray-100 rounded-lg">
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
              <input
                type="text"
                value={calSearch}
                onChange={e => setCalSearch(e.target.value)}
                placeholder="Search topics..."
                className="w-64 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* List view */}
            {calViewMode === 'list' && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
                {filteredCalEntries.length === 0 ? (
                  <div className="p-8 text-center text-gray-400 text-sm">
                    No calendar entries. Use "Add from Topical Map" to select topics and distribute them across a date range.
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-left">
                        <th className="px-4 py-3 w-10">
                          <input
                            type="checkbox"
                            checked={filteredCalEntries.length > 0 && filteredCalEntries.every(e => selectedCalIds.has(e.id))}
                            onChange={toggleCalSelectAll}
                            className="rounded border-gray-300"
                          />
                        </th>
                        <th className="px-4 py-3 font-medium text-gray-500 uppercase text-xs">Title</th>
                        <th className="px-4 py-3 font-medium text-gray-500 uppercase text-xs">Keyword</th>
                        <th className="px-4 py-3 font-medium text-gray-500 uppercase text-xs">Category</th>
                        <th className="px-4 py-3 font-medium text-gray-500 uppercase text-xs">Date</th>
                        <th className="px-4 py-3 font-medium text-gray-500 uppercase text-xs">Status</th>
                        <th className="px-4 py-3 font-medium text-gray-500 uppercase text-xs">Priority</th>
                        <th className="px-4 py-3 font-medium text-gray-500 uppercase text-xs w-20">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredCalEntries.map(e => (
                        <tr key={e.id} className={`hover:bg-gray-50 ${selectedCalIds.has(e.id) ? 'bg-blue-50' : ''}`}>
                          <td className="px-4 py-2.5">
                            <input
                              type="checkbox"
                              checked={selectedCalIds.has(e.id)}
                              onChange={() => toggleCalSelect(e.id)}
                              className="rounded border-gray-300"
                            />
                          </td>
                          <td className="px-4 py-2.5 font-medium text-gray-900 truncate max-w-[250px]">{e.title}</td>
                          <td className="px-4 py-2.5 text-gray-600 text-xs truncate max-w-[200px]">{e.keyword}</td>
                          <td className="px-4 py-2.5 text-gray-600 text-xs">{e.category}</td>
                          <td className="px-4 py-2.5 text-gray-600 text-xs">{formatDate(e.scheduled_date)}</td>
                          <td className="px-4 py-2.5">
                            <select
                              value={e.status}
                              onChange={ev => handleUpdateEntryStatus(e.id, ev.target.value)}
                              className={`px-2 py-0.5 rounded-full text-xs font-medium border-0 ${statusColor(e.status)}`}
                            >
                              {['scheduled', 'rewrite', 'writing', 'written', 'images', 'ready', 'published'].map(s => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-4 py-2.5">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priorityBadge(e.priority)}`}>
                              {e.priority}
                            </span>
                          </td>
                          <td className="px-4 py-2.5">
                            <button
                              onClick={() => handleDeleteEntry(e.id)}
                              className="text-red-400 hover:text-red-600"
                              title="Remove from calendar"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* Calendar grid view */}
            {calViewMode === 'calendar' && (
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-lg overflow-hidden">
                  {/* Day headers */}
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                    <div key={d} className="bg-gray-50 px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                      {d}
                    </div>
                  ))}
                  {/* Calendar cells */}
                  {calendarGrid.map((day, i) => {
                    const dateStr = day
                      ? `${calYear}-${String(calMonthNum + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
                      : null;
                    const dayEntries = dateStr ? (entriesByDate[dateStr] || []) : [];
                    const isToday = dateStr === new Date().toISOString().split('T')[0];
                    return (
                      <div
                        key={i}
                        className={`bg-white min-h-[100px] p-1.5 ${!day ? 'bg-gray-50' : ''} ${isToday ? 'ring-2 ring-inset ring-blue-400' : ''}`}
                      >
                        {day && (
                          <>
                            <div className={`text-xs font-medium mb-1 ${isToday ? 'text-blue-600' : 'text-gray-500'}`}>
                              {day}
                            </div>
                            <div className="space-y-0.5">
                              {dayEntries.slice(0, 3).map(e => (
                                <div
                                  key={e.id}
                                  className="px-1.5 py-0.5 bg-teal-50 border border-teal-200 rounded text-[10px] text-teal-800 truncate cursor-default"
                                  title={`${e.title} (${e.status})`}
                                >
                                  {e.title}
                                </div>
                              ))}
                              {dayEntries.length > 3 && (
                                <div className="text-[10px] text-gray-400 pl-1">+{dayEntries.length - 3} more</div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Add Topic Modal */}
            {showAddTopic && (
              <div
                className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                onClick={() => !addingTopic && setShowAddTopic(false)}
              >
                <div
                  className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Topic to Topical Map</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Topic *</label>
                      <input
                        type="text"
                        value={newTopic.topic}
                        onChange={(e) => setNewTopic({ ...newTopic, topic: e.target.value })}
                        placeholder="e.g. Inpatient Rehab for Veterans"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        autoFocus
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Target Keyword</label>
                      <input
                        type="text"
                        value={newTopic.target_keyword}
                        onChange={(e) => setNewTopic({ ...newTopic, target_keyword: e.target.value })}
                        placeholder="e.g. inpatient rehab veterans"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                      <input
                        type="text"
                        value={newTopic.category}
                        onChange={(e) => setNewTopic({ ...newTopic, category: e.target.value })}
                        placeholder="e.g. Treatment Programs"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Intent</label>
                        <select
                          value={newTopic.search_intent}
                          onChange={(e) => setNewTopic({ ...newTopic, search_intent: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="informational">Informational</option>
                          <option value="commercial">Commercial</option>
                          <option value="transactional">Transactional</option>
                          <option value="navigational">Navigational</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
                        <select
                          value={newTopic.priority}
                          onChange={(e) => setNewTopic({ ...newTopic, priority: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                          <option value="critical">Critical</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Section</label>
                        <select
                          value={newTopic.section_type}
                          onChange={(e) => setNewTopic({ ...newTopic, section_type: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="core">Core</option>
                          <option value="outer">Outer</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Phase</label>
                        <select
                          value={newTopic.phase}
                          onChange={(e) => setNewTopic({ ...newTopic, phase: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="foundation">Foundation</option>
                          <option value="expansion">Expansion</option>
                          <option value="depth">Depth</option>
                          <option value="authority">Authority</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end gap-2">
                    <button
                      onClick={() => setShowAddTopic(false)}
                      disabled={addingTopic}
                      className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddTopic}
                      disabled={addingTopic || !newTopic.topic.trim()}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                    >
                      {addingTopic ? 'Adding…' : 'Add Topic'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Topic Picker Modal */}
            <TopicPickerModal
              projectId={pid || 0}
              isOpen={showTopicPicker}
              onClose={() => setShowTopicPicker(false)}
              onSelect={handleTopicsSelected}
              preloadedTopics={topics.map(t => ({
                id: t.id,
                title: t.topic || t.title_tag || '(untitled)',
                primary_keyword: t.target_keyword || null,
                generator_type: t.category || t.generation_method || '',
                priority_score: t.priority === 'critical' ? 90 : t.priority === 'high' ? 75 : t.priority === 'medium' ? 50 : 25,
              }))}
            />
          </div>
        )}
      </div>
    </Layout>
  );
}
